import { useMemo, useState } from "react";
import GameShell from "../GameShell";

/**
 * Vila dos NPCs — ADOS-2 inspired social scenarios.
 * 8 scenarios with 3 response options. We classify responses into:
 *   - prosocial (best fit)
 *   - neutral
 *   - atypical / withdrawn
 * Not "right/wrong" — but we still give a positive score; the metrics
 * track distribution of response types for clinical interpretation.
 */
export default function NPCVillage() {
  return (
    <GameShell gameId="npc_village" title="Vila dos NPCs">
      {({ onFinish }) => <Game onFinish={onFinish} />}
    </GameShell>
  );
}

const SCENARIOS = [
  {
    npc: "👧",
    text: "Uma menina parece triste no canto da praça.",
    options: [
      { text: "Chegar perto e perguntar o que aconteceu", type: "prosocial" },
      { text: "Olhar de longe e ir embora", type: "neutral" },
      { text: "Continuar o que estava fazendo", type: "atypical" },
    ],
  },
  {
    npc: "👴",
    text: "Um senhor deixou cair as compras dele.",
    options: [
      { text: "Ajudar a recolher", type: "prosocial" },
      { text: "Avisar outra pessoa", type: "neutral" },
      { text: "Continuar andando", type: "atypical" },
    ],
  },
  {
    npc: "👦",
    text: "Um amigo te chama para brincar mas você está cansado.",
    options: [
      { text: "Dizer 'agora não, mais tarde podemos brincar'", type: "prosocial" },
      { text: "Apenas dizer 'não'", type: "neutral" },
      { text: "Não responder", type: "atypical" },
    ],
  },
  {
    npc: "👩",
    text: "Uma moça aponta para algo lá longe e olha para você.",
    options: [
      { text: "Olhar na direção que ela aponta", type: "prosocial" },
      { text: "Olhar só para ela", type: "neutral" },
      { text: "Não olhar", type: "atypical" },
    ],
  },
  {
    npc: "🧒",
    text: "Você ganhou um presente de um colega.",
    options: [
      { text: "Agradecer com sorriso", type: "prosocial" },
      { text: "Aceitar sem dizer nada", type: "neutral" },
      { text: "Guardar e ir brincar", type: "atypical" },
    ],
  },
  {
    npc: "👨",
    text: "Um adulto fala seu nome.",
    options: [
      { text: "Olhar e responder", type: "prosocial" },
      { text: "Levantar a cabeça só", type: "neutral" },
      { text: "Continuar olhando para o brinquedo", type: "atypical" },
    ],
  },
  {
    npc: "🧓",
    text: "Você quer entrar num jogo onde já tem outras crianças.",
    options: [
      { text: "Pedir 'posso jogar com vocês?'", type: "prosocial" },
      { text: "Ficar perto esperando", type: "neutral" },
      { text: "Jogar sozinho em outro lugar", type: "atypical" },
    ],
  },
  {
    npc: "👶",
    text: "Algo inesperado acontece (uma surpresa).",
    options: [
      { text: "Rir e contar para alguém", type: "prosocial" },
      { text: "Reagir com calma", type: "neutral" },
      { text: "Ficar muito incomodado", type: "atypical" },
    ],
  },
];

function Game({ onFinish }) {
  const [idx, setIdx] = useState(0);
  const [counts, setCounts] = useState({ prosocial: 0, neutral: 0, atypical: 0 });
  const [reactionTimes, setReactionTimes] = useState([]);
  const [started] = useState(() => performance.now());
  const [tStart, setTStart] = useState(() => performance.now());
  const [feedback, setFeedback] = useState(false);

  const sc = SCENARIOS[idx];
  const order = useMemo(
    () => sc.options.map((_, i) => i).sort(() => Math.random() - 0.5),
    [idx] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const choose = (opt) => {
    if (feedback) return;
    const rt = performance.now() - tStart;
    setReactionTimes((r) => [...r, rt]);
    setCounts((c) => ({ ...c, [opt.type]: c[opt.type] + 1 }));
    setFeedback(true);
    setTimeout(() => {
      const next = idx + 1;
      if (next >= SCENARIOS.length) {
        const elapsed = performance.now() - started;
        const newCounts = { ...counts, [opt.type]: counts[opt.type] + 1 };
        const avg =
          [...reactionTimes, rt].reduce((a, b) => a + b, 0) / (reactionTimes.length + 1);
        const score = Math.max(
          0,
          Math.round(newCounts.prosocial * 12 + newCounts.neutral * 6 + newCounts.atypical * 2)
        );
        onFinish({
          score,
          duration_ms: Math.round(elapsed),
          metrics: {
            scenarios: SCENARIOS.length,
            prosocial: newCounts.prosocial,
            neutral: newCounts.neutral,
            atypical: newCounts.atypical,
            avg_reaction_ms: Math.round(avg),
          },
        });
      } else {
        setFeedback(false);
        setIdx(next);
        setTStart(performance.now());
      }
    }, 500);
  };

  return (
    <div className="mt-4 max-w-2xl mx-auto" data-testid="npc-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">O que você faria?</div>
        <div className="text-xs mt-1 opacity-80">Cena {idx + 1} / {SCENARIOS.length}</div>
      </div>
      <div className="kid-card pop-in" key={idx}>
        <div className="flex items-center gap-4 mb-4">
          <div className="text-6xl">{sc.npc}</div>
          <div className="text-lg text-slate-800 leading-snug">{sc.text}</div>
        </div>
        <div className="space-y-3">
          {order.map((i) => (
            <button
              key={i}
              onClick={() => choose(sc.options[i])}
              className="w-full text-left bg-sky-50 hover:bg-sky-100 active:scale-[0.98] rounded-2xl px-4 py-3 text-slate-800 font-semibold transition border-2 border-transparent hover:border-sky-300"
              data-testid={`npc-opt-${i}`}
            >
              {sc.options[i].text}
            </button>
          ))}
        </div>
        {feedback && (
          <div className="text-center text-3xl mt-3 pop-in" data-testid="npc-feedback">
            👍
          </div>
        )}
      </div>
    </div>
  );
}
